import { apiRequest, SUPABASE_URL, SUPABASE_ANON_KEY } from './apiUtils';
import { PayoutOrder, Payout, PayoutStatus, RecipientType } from '@/models/Order';

export interface PayoutGroupRequest {
  recipient_type: string;
  recipient_id: number | null;
  payout_order_ids: number[];
}

export interface ProcessPayoutsRequest {
  order_id: number;
}

export interface PayoutOrderQueryParams {
  status?: PayoutStatus;
  recipient_type?: RecipientType;
  recipient_id?: number;
  order_id?: number;
  payout_id?: number;
  management_company_id?: number;
  show_all?: boolean;
  show_none?: boolean;
  exclude_paid_payouts?: boolean;
  // Pagination parameters
  limit?: number;
  offset?: number;
  page?: number;
}

export interface PayoutQueryParams {
  status?: string;
  management_company_id?: number;
  show_all?: boolean;
  show_none?: boolean;
  // Pagination parameters
  limit?: number;
  offset?: number;
  page?: number;
}

// Pagination response interfaces
export interface PaginatedPayoutOrdersResponse {
  data: PayoutOrder[];
  metadata: {
    total_count: number;
    limit: number;
    offset: number;
    page: number;
    totalPages: number;
  };
}

export interface PaginatedPayoutsResponse {
  data: Payout[];
  metadata: {
    total_count: number;
    limit: number;
    offset: number;
    page: number;
    totalPages: number;
  };
}

export const payoutApi = {
  /**
   * Get payout orders with optional filtering and pagination
   */
  async getPayoutOrders(params?: PayoutOrderQueryParams): Promise<PayoutOrder[] | PaginatedPayoutOrdersResponse> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params?.status) queryParams.append('status', params.status);
      if (params?.recipient_type) queryParams.append('recipient_type', params.recipient_type);
      if (params?.recipient_id) queryParams.append('recipient_id', params.recipient_id.toString());
      if (params?.order_id) queryParams.append('order_id', params.order_id.toString());
      if (params?.payout_id) queryParams.append('payout_id', params.payout_id.toString());
      
      // Add role-based filtering parameters
      if (params?.management_company_id !== undefined) {
        queryParams.append('management_company_id', params.management_company_id.toString());
      }
      if (params?.show_all !== undefined) {
        queryParams.append('show_all', params.show_all.toString());
      }
      if (params?.show_none !== undefined) {
        queryParams.append('show_none', params.show_none.toString());
      }
      if (params?.exclude_paid_payouts !== undefined) {
        queryParams.append('exclude_paid_payouts', params.exclude_paid_payouts.toString());
      }
      
      // Add pagination parameters
      if (params?.limit !== undefined) {
        queryParams.append('limit', params.limit.toString());
      }
      if (params?.offset !== undefined) {
        queryParams.append('offset', params.offset.toString());
      }
      if (params?.page !== undefined) {
        queryParams.append('page', params.page.toString());
      }
      
      const url = `${SUPABASE_URL}/payouts?${queryParams.toString()}`;
      return await apiRequest<PayoutOrder[] | PaginatedPayoutOrdersResponse>(url);
    } catch (error) {
      console.error('Error fetching payout orders:', error);
      throw error;
    }
  },

  /**
   * Get ALL payout orders without pagination (for CSV export)
   */
  async getAllPayoutOrders(params?: Omit<PayoutOrderQueryParams, 'limit' | 'offset' | 'page'>): Promise<PayoutOrder[]> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params?.status) queryParams.append('status', params.status);
      if (params?.recipient_type) queryParams.append('recipient_type', params.recipient_type);
      if (params?.recipient_id) queryParams.append('recipient_id', params.recipient_id.toString());
      if (params?.order_id) queryParams.append('order_id', params.order_id.toString());
      if (params?.payout_id) queryParams.append('payout_id', params.payout_id.toString());
      
      // Add role-based filtering parameters
      if (params?.management_company_id !== undefined) {
        queryParams.append('management_company_id', params.management_company_id.toString());
      }
      if (params?.show_all !== undefined) {
        queryParams.append('show_all', params.show_all.toString());
      }
      if (params?.show_none !== undefined) {
        queryParams.append('show_none', params.show_none.toString());
      }
      if (params?.exclude_paid_payouts !== undefined) {
        queryParams.append('exclude_paid_payouts', params.exclude_paid_payouts.toString());
      }
      
      // Explicitly don't add pagination parameters to get ALL data
      
      const url = `${SUPABASE_URL}/payouts?${queryParams.toString()}`;
      return await apiRequest<PayoutOrder[]>(url);
    } catch (error) {
      console.error('Error fetching all payout orders:', error);
      throw error;
    }
  },

  /**
   * Create payout orders for an order
   */
  async createPayoutOrders(orderId: number): Promise<PayoutOrder[]> {
    try {
      const url = `${SUPABASE_URL}/payouts`;
      return await apiRequest<PayoutOrder[]>(url, {
        method: 'POST',
        body: { order_id: orderId }
      });
    } catch (error) {
      console.error('Error creating payout orders:', error);
      throw error;
    }
  },

  /**
   * Process payouts for an order
   * This generates payout orders based on approved orders and their fees
   */
  async processPayouts(request: ProcessPayoutsRequest): Promise<PayoutOrder[]> {
    try {
      const url = `${SUPABASE_URL}/payouts/process`;
      return await apiRequest<PayoutOrder[]>(url, {
        method: 'POST',
        body: request
      });
    } catch (error) {
      console.error('Error processing payouts:', error);
      throw error;
    }
  },

  /**
   * Group payout orders into a single payout
   */
  async groupPayoutOrders(request: PayoutGroupRequest): Promise<Payout> {
    try {
      const url = `${SUPABASE_URL}/payouts/group`;
      
      // Log the request details for debugging
      console.log('Grouping payout orders with request:', request);
      
      // Make sure we're only sending payout orders that are in 'ready_to_be_paid' status
      const response = await apiRequest<Payout>(url, {
        method: 'POST',
        body: request
      });
      
      console.log('Successful payout grouping response:', response);
      return response;
    } catch (error) {
      console.error('Error grouping payout orders:', error);
      throw error;
    }
  },

  /**
   * Update payout order status
   */
  async updatePayoutOrderStatus(payoutOrderId: number, status: string): Promise<PayoutOrder> {
    try {
      // Check if the status is 'paid' and the current payout order status is already 'paid'
      if (status === 'paid') {
        const payoutOrders = await this.getPayoutOrders();
        const existingOrder = payoutOrders.find(order => order.id === payoutOrderId);
        
        if (existingOrder && existingOrder.status === 'paid') {
          throw new Error('Cannot modify a payout order that is already paid');
        }
      }
      
      const url = `${SUPABASE_URL}/payouts/order/${payoutOrderId}`;
      return await apiRequest<PayoutOrder>(url, {
        method: 'PATCH',
        body: { status }
      });
    } catch (error) {
      console.error(`Error updating payout order ${payoutOrderId}:`, error);
      throw error;
    }
  },

  /**
   * Update payout status
   */
  async updatePayoutStatus(payoutId: number, status: string, paymentReference?: string): Promise<Payout> {
    try {
      // Check if the status is 'paid' and the current payout status is already 'paid'
      if (status === 'paid') {
        const payouts = await this.getPayouts();
        const existingPayout = payouts.find(payout => payout.id === payoutId);
        
        if (existingPayout && existingPayout.status === 'paid') {
          throw new Error('Cannot modify a payout that is already paid');
        }
      }
      
      const url = `${SUPABASE_URL}/payouts/payout/${payoutId}`;
      const body: Record<string, any> = { status };
      
      if (paymentReference) {
        body.payment_reference = paymentReference;
      }
      
      return await apiRequest<Payout>(url, {
        method: 'PATCH',
        body
      });
    } catch (error) {
      console.error(`Error updating payout ${payoutId}:`, error);
      throw error;
    }
  },
  
  /**
   * Get all payouts with optional filtering and pagination
   */
  async getPayouts(status?: string, filters?: PayoutQueryParams): Promise<Payout[] | PaginatedPayoutsResponse> {
    try {
      const queryParams = new URLSearchParams();
      
      if (status) queryParams.append('status', status);
      
      // Add role-based filtering parameters
      if (filters?.management_company_id !== undefined) {
        queryParams.append('management_company_id', filters.management_company_id.toString());
      }
      if (filters?.show_all !== undefined) {
        queryParams.append('show_all', filters.show_all.toString());
      }
      if (filters?.show_none !== undefined) {
        queryParams.append('show_none', filters.show_none.toString());
      }
      
      // Add pagination parameters
      if (filters?.limit !== undefined) {
        queryParams.append('limit', filters.limit.toString());
      }
      if (filters?.offset !== undefined) {
        queryParams.append('offset', filters.offset.toString());
      }
      if (filters?.page !== undefined) {
        queryParams.append('page', filters.page.toString());
      }
      
      const url = `${SUPABASE_URL}/payouts/all-payouts?${queryParams.toString()}`;
      return await apiRequest<Payout[] | PaginatedPayoutsResponse>(url);
    } catch (error) {
      console.error('Error fetching payouts:', error);
      throw error;
    }
  },

  /**
   * Update approval status for payout orders
   */
  async updateApprovalStatus(payoutOrderIds: number[], approvalStatus: 'pending' | 'approved' | 'rejected', userId?: number): Promise<any> {
    try {
      const url = `${SUPABASE_URL}/payouts/bulk-upload`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          payout_order_ids: payoutOrderIds,
          approval_status: approvalStatus,
          user_id: userId
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update approval status');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating approval status:', error);
      throw error;
    }
  }
};
